import { ChangeDetectionStrategy, Component, ElementRef, TemplateRef, ViewEncapsulation, computed, inject, input, isSignal, output, signal } from '@angular/core';
import { CommonModule, DOCUMENT, NgComponentOutlet } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Command } from '@workbuddy/ui/model';
import { ButtonDropdownComponent, ButtonSelectComponent, ButtonSize, ButtonSwitchComponent, ButtonTabComponent } from '@workbuddy/ui/v2/components/button';
import { IconPickerComponent } from '@workbuddy/ui/components/icon.picker';
import { ALL_DESIGN_TONES, DesignTone } from '@workbuddy/ui/v2/design-system';
import { SettingsPanelMenuItem, SettingsPanelMenuSearch, SettingsPanelMode, SettingsPanelDropdownItem, SettingsPanelSelectOption, SettingsPanelActionItem, SettingsPanelInlineComponentItem, SettingsPanelTonePickerItem, SettingsPanelDisplayPreview, SettingsPanelDisplayValue, SettingsPanelVisibility, SettingsPanelErrorMessage } from '../model';
import { SettingsPanelForm, SettingsPanelValidationError } from '../settings.panel.form';
import { AutofocusOnRenderDirective } from './autofocus.directive';
import { TonePickerComponent } from './tone.picker.component';

@Component({
  selector: 'settings-panel-menu',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonDropdownComponent, ButtonSelectComponent, ButtonSwitchComponent, ButtonTabComponent, AutofocusOnRenderDirective, IconPickerComponent, TonePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './settings.panel.menu.component.html',
  host: {
    '(keydown.arrowdown)': 'onArrow($event, 1)',
    '(keydown.arrowup)': 'onArrow($event, -1)',
  },
})
export class SettingsPanelMenuComponent {

  public $items = input.required<SettingsPanelMenuItem[]>({ alias: 'items' });
  public $renderers = input<Map<string, TemplateRef<unknown>>>(new Map(), { alias: 'renderers' });
  public $formGroup = input<FormGroup | null>(null, { alias: 'formGroup' });
  public $panel = input<SettingsPanelForm | null>(null, { alias: 'panel' });
  public $showErrors = input<boolean>(true, { alias: 'showErrors' });
  public $search = input<SettingsPanelMenuSearch | undefined>(undefined, { alias: 'search' });
  public $mode = input<SettingsPanelMode>(SettingsPanelMode.Sidebar, { alias: 'mode' });

  public itemClicked = output<SettingsPanelMenuItem>();

  protected readonly ButtonSize = ButtonSize;

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly menuId = `sp-menu-${Math.random().toString(36).slice(2, 9)}`;

  private readonly _query = signal('');
  protected readonly $query = this._query.asReadonly();

  private readonly _highlightIndex = signal(0);
  protected readonly $highlightIndex = this._highlightIndex.asReadonly();

  protected readonly $isCommandPalette = computed(() => this.$mode() === SettingsPanelMode.CommandPalette);
  protected readonly $activeDescendant = computed(() => this.$isCommandPalette() && !!this.$search()?.enabled);

  protected $visibleItems = computed(() => {

    const items = this.$items().filter(item => !this.resolveVisibility((item as any).hidden));
    const query = this.$search()?.enabled ? this._query().trim().toLowerCase() : '';
    if (!query) return this.stripEmptyGroups(items);

    const filtered = items.filter(item => {
      if (item.type === 'divider') return false;
      if (item.type === 'group') return true;
      const label = (item as { label?: string }).label;
      return !!label && label.toLowerCase().includes(query);
    });

    return this.stripEmptyGroups(filtered);
  });

  protected readonly $selectableItems = computed(() => {

    return this.$visibleItems().filter(item => item.type !== 'divider' && item.type !== 'compound' && item.type !== 'group');
  });

  private stripEmptyGroups(items: SettingsPanelMenuItem[]): SettingsPanelMenuItem[] {

    const result: SettingsPanelMenuItem[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type !== 'group') { result.push(item); continue; }
      const next = items[i + 1];
      if (!next || next.type === 'group' || next.type === 'divider') continue;
      result.push(item);
    }
    return result;
  }

  protected readonly $activeDescendantId = computed(() => {

    if (!this.$activeDescendant()) return null;
    const items = this.$selectableItems();
    if (!items.length) return null;
    const index = Math.min(this._highlightIndex(), items.length - 1);
    const id = (items[index] as { id?: string }).id;
    return id ? `${this.menuId}-${id}` : null;
  });

  protected rowId(item: SettingsPanelMenuItem): string | null {

    if (item.type === 'divider' || item.type === 'compound' || item.type === 'group') return null;
    return `${this.menuId}-${(item as { id: string }).id}`;
  }

  protected isHighlighted(item: SettingsPanelMenuItem): boolean {

    if (!this.$activeDescendant()) return false;
    const selectable = this.$selectableItems();
    const index = Math.min(this._highlightIndex(), Math.max(selectable.length - 1, 0));
    return selectable[index] === item;
  }

  protected onQueryInput(event: Event): void {

    this._query.set((event.target as HTMLInputElement).value);
    this._highlightIndex.set(0);
  }

  protected onRowMousedown(event: MouseEvent): void {

    if (!this.$activeDescendant()) return;
    event.preventDefault();
  }

  protected onSearchKeydown(event: KeyboardEvent): void {

    if (!this.$activeDescendant()) return;

    const items = this.$selectableItems();
    if (!items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._highlightIndex.update(i => (i + 1) % items.length);
        this.scrollHighlightedItemIntoView();
        return;
      case 'ArrowUp':
        event.preventDefault();
        this._highlightIndex.update(i => (i - 1 + items.length) % items.length);
        this.scrollHighlightedItemIntoView();
        return;
      case 'Enter': {
        event.preventDefault();
        const index = Math.min(this._highlightIndex(), items.length - 1);
        this.onItemClick(items[index]);
        return;
      }
    }
  }

  protected hasRenderer(type: string): boolean {

    return this.$renderers().has(type);
  }

  protected getRenderer(type: string): TemplateRef<unknown> | null {

    return this.$renderers().get(type) ?? null;
  }

  protected resolveVisibility(value?: SettingsPanelVisibility): boolean {

    if (value === undefined || value === null) return false;
    try { return typeof value === 'function' ? value() : value; } catch { return false; }
  }

  protected getErrorMessage(item: SettingsPanelMenuItem): string | null {

    if (!this.$showErrors()) return null;
    if (item.type === 'divider' || item.type === 'compound' || item.type === 'group' || !item.control) return null;

    const errors = this.getFieldErrors(item.id);
    if (!errors.length) return null;

    const configuredMessage = this.resolveErrorMessage((item as any).errorMessage, errors);
    if (configuredMessage) return configuredMessage;

    if (errors.some(error => error['unique'])) return `${item.label} already taken`;
    if (errors.some(error => error['required'])) return `${item.label} is required`;

    return 'Invalid value';
  }

  /**
   * ArrowUp/Down moves focus between rows in the panel. Each row has one
   * primary focusable inside it; we find the row containing the currently-
   * focused element and jump to the first focusable in the next/previous row.
   */
  protected onArrow(event: KeyboardEvent, direction: 1 | -1): void {

    if (this.$activeDescendant()) return;

    const rows = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.settings-item-block'));
    if (rows.length === 0) return;

    const active = this.document.activeElement as HTMLElement | null;
    if (!active) return;

    // Find which row currently holds focus.
    const currentRowIdx = rows.findIndex(row => row.contains(active));
    if (currentRowIdx < 0) return;

    // Walk to the next row that has a focusable element.
    let targetIdx = currentRowIdx + direction;
    while (targetIdx >= 0 && targetIdx < rows.length) {
      const targetRow = rows[targetIdx];
      const focusable = this.firstFocusableIn(targetRow);
      if (focusable) {
        event.preventDefault();
        event.stopPropagation();
        focusable.focus({ preventScroll: true });
        this.scrollRowIntoView(targetRow);
        return;
      }
      targetIdx += direction;
    }
  }

  private firstFocusableIn(row: HTMLElement): HTMLElement | null {

    return row.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex="0"]'
    );
  }

  private scrollHighlightedItemIntoView(): void {

    this.document.defaultView?.requestAnimationFrame(() => {
      const activeId = this.$activeDescendantId();
      if (!activeId) return;

      const row = this.host.nativeElement.querySelector<HTMLElement>(`#${CSS.escape(activeId)}`);
      if (!row) return;

      this.scrollRowIntoView(row);
    });
  }

  private scrollRowIntoView(row: HTMLElement): void {

    const scrollRoot = row.closest<HTMLElement>('.settings-menu-list');
    if (!scrollRoot) {
      row.scrollIntoView({ block: 'nearest' });
      return;
    }

    const rootRect = scrollRoot.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const padding = 8;

    if (rowRect.top < rootRect.top + padding) {
      scrollRoot.scrollTop -= rootRect.top + padding - rowRect.top;
      return;
    }

    if (rowRect.bottom > rootRect.bottom - padding) {
      scrollRoot.scrollTop += rowRect.bottom - (rootRect.bottom - padding);
    }
  }

  protected onItemClick(item: SettingsPanelMenuItem) {

    if (item.type === 'divider' || item.type === 'compound') return;

    if (item.type === 'action') {
      (item as SettingsPanelActionItem).action?.();
    }

    this.itemClicked.emit(item);
  }


  private _dropdownCommandsCache = new Map<string, Command[]>();

  private resolveDropdownOptions(item: SettingsPanelDropdownItem): SettingsPanelSelectOption[] {

    return isSignal(item.options) ? item.options() : item.options ?? [];
  }

  protected getDropdownCommands(item: SettingsPanelDropdownItem): Command[] {

    if (isSignal(item.options)) {
      return this.resolveDropdownOptions(item).map(opt => new Command({ _id: String(opt.id), label: opt.label, iconLeft: opt.icon, group: opt.group, data: opt.id }));
    }

    let commands = this._dropdownCommandsCache.get(item.id);
    if (!commands) {
      commands = (item.options ?? []).map(opt => new Command({ _id: String(opt.id), label: opt.label, iconLeft: opt.icon, group: opt.group, data: opt.id }));
      this._dropdownCommandsCache.set(item.id, commands);
    }
    return commands;
  }


  protected getInlineComponentInputs(item: SettingsPanelInlineComponentItem): Record<string, unknown> {

    return { formGroup: this.$formGroup(), ...item.inputs };
  }

  protected resolveTonePickerTones(item: SettingsPanelTonePickerItem): DesignTone[] {

    return (item.tones?.length ? item.tones : ALL_DESIGN_TONES) as DesignTone[];
  }

  protected resolveDisplay(item: SettingsPanelMenuItem): { text: string; preview: SettingsPanelDisplayPreview | null } {

    if (item.type === 'divider' || item.type === 'compound' || item.type === 'group') return { text: '', preview: null };

    const value: SettingsPanelDisplayValue | undefined = item.displayValue$?.() ?? (item as any).value;
    if (value == null || value === '') return { text: '', preview: null };
    if (typeof value === 'string') return { text: value, preview: null };
    return { text: value.text ?? '', preview: { icon: value.icon, tone: value.tone, text: value.text } };
  }

  private getFieldErrors(fieldId: string): SettingsPanelValidationError[] {

    return (this.$panel()?.errors() ?? []).filter(error => this.matchesFieldError(fieldId, error));
  }

  private matchesFieldError(fieldId: string, error: SettingsPanelValidationError): boolean {

    const path = String(error['field'] ?? error['path'] ?? '');
    return path === fieldId || path.startsWith(`${fieldId}.`);
  }

  private resolveErrorMessage(config: SettingsPanelErrorMessage | undefined, errors: SettingsPanelValidationError[]): string | null {

    if (!config) return null;
    if (typeof config === 'string') return config;
    if (typeof config === 'function') return config(errors);

    const errorKeys = errors.flatMap(error => Object.keys(error).filter(key => key !== 'field' && key !== 'path'));
    return errorKeys.map(key => config[key]).find(Boolean) ?? null;
  }

}
